import _ from 'underscore';
import ko from 'knockout';
import kendo from '@bingads-webui/kendo/kendo.core';
import DatePicker from '../binding/date-picker';

const intTestReg = /^\s*[+-]?\s*\d+(,\d{3})*\s*$/;

export class PredicateVM {
  constructor(options) {
    this.options = options || {};

    this.propertyText = ko.observable(this.options.propertyText);
    this.propertyValue = ko.observable(this.options.propertyValue);

    this.operator = ko.observable(this.options.operator);

    this.metadata = ko.computed(() =>
      this.options.filterSchema.getFilterProperty(this.propertyValue()), this);

    this.description = ko.observable(this.options.description ||
      this.metadata().description || this.options.propertyValue);
    this.value = ko.observable(this.mapValue(this.options.value));
    this.values = ko.observableArray(_.isArray(this.options.value) ? this.options.value : []);
    this.isPercentValue = ko.computed(() => this.metadata().isPercentValue || false, this);

    this.operators = ko.computed(this.getOperators, this);
    this.enumOptions = ko.computed(this.getOptions, this);
    this.groupedOptions = ko.computed(this.getGroupedOptions, this);

    this.valueChanged = ko.observable(false);
    this.value.subscribe(this.onValueChange.bind(this));
    this.values.subscribe(this.onValueChange.bind(this));

    this.validationResult = ko.computed(this.validate, this);
    this.template = ko.computed(this.getTemplate, this);
    this.isValueValid = ko.computed(this.isValueValid, this);

    this.selectProperty = (selected) => {
      this.propertyValue(selected.name);
      this.propertyText(selected.localized);
    };
  }

  reset() {
    this.operator(this.metadata().operators[0].replace(/^\$/, ''));
    this.description(this.metadata().description || this.propertyValue());
    this.value(this.metadata().type === 'boolean' ? false : undefined);
    this.values([]);
    this.valueChanged(false);
    DatePicker.dispose(this.value);
  }

  onValueChange() {
    if (!this.valueChanged()) {
      this.valueChanged(true);
    }
  }

  isValueValid() {
    return !this.valueChanged() || this.validationResult().isValid;
  }

  getExpression() {
    const value = this.getExpressionValue();
    if (_.isNull(value)) {
      return null;
    }
    return {
      property: this.propertyValue(),
      operator: this.operator(),
      value: this.getExpressionValue(),
    };
  }

  isValueExists() {
    return (!_.isUndefined(this.value()) && !_.isEqual(this.value(), []) && this.value() !== '')
      || (!_.isArray(this.values()) && !_.isUndefined(this.values()))
      || (_.isArray(this.values()) && !_.isEmpty(this.values()));
  }

  /**
   * Gets expression value
   * @private
   * @returns {any} the expression value
   */
  getExpressionValue() {
    let value = this.value();
    const values = this.values();

    if (!this.validationResult().isValid) {
      return null;
    }

    const { type } = this.metadata();
    if (type === 'number' && _.isString(value)) {
      value = this.options.i18n.parseDecimal(value);
    } else if (type === 'integer' && _.isString(value)) {
      // use parseFloat here for 64bit, kendo.pareseInt is for 32bit
      value = kendo.parseFloat(value);
    }

    if (type === 'boolean') {
      if (_.isBoolean(value)) {
        return value;
      }
      return value === 'true';
    } else if (this.metadata().enum) {
      return _.isArray(values) ? values : [values];
    } else if ((_.isNumber(value) && !_.isNaN(value)) || _.isBoolean(value)) {
      return value;
    } else if (!value && !_.isEmpty(values)) {
      return _.uniq(_.flatten(values));
    }

    return value || null;
  }

  /**
   * Gets the operators
   * @private
   * @returns {Array<Object>} arrays of operators
   */
  getOperators() {
    const rawOperators = this.metadata().operators;
    return _.map(rawOperators, op => ({
      operator: op,
      localized: this.options.i18nConverter.convertOperator(
        op,
        this.metadata().operatorDisplayNameMap
      ),
    }));
  }

  /**
   * Gets the template
   * @private
   * @returns {Function} - the template
   */
  getTemplate() {
    const { templates } = this.options;

    if (!templates) {
      return null;
    }

    if ((_.isEmpty(this.metadata().enum) && _.isEmpty(this.metadata().chooseType)) || this.metadata().type === 'category') {
      return templates[this.metadata().type];
    }

    if (this.metadata().chooseType === 'radio-boolean') {
      return templates.radioBoolean;
    }

    if (this.metadata().chooseType === 'textarea') {
      return templates.textarea;
    }

    if (this.metadata().chooseType === 'checkbox') {
      return templates.checkboxEnum;
    } else if (this.metadata().chooseType === 'select') {
      if (this.metadata().type === 'boolean') {
        return templates.selectBoolean;
      }
      return templates.selectEnum;
    }
    return templates.radioEnum;
  }

  /**
   * Gets the options
   * @private
   * @returns {Array} - the array of options
   */
  getOptions() {
    const { type, chooseType } = this.metadata();
    if (type === 'boolean') {
      if (chooseType === 'select') {
        return this.getBooleanOptionsForEnumSelect();
      }
      return this.getBooleanOptions();
    } else if (!_.isEmpty(this.metadata().enum)) {
      return this.getEnumOptions();
    }

    return [];
  }

  /**
   * Gets the boolean options
   * @private
   * @returns {Array<Object>} - the array of boolean options
   */
  getBooleanOptions() {
    return [{
      value: true,
      localized: this.options.i18nConverter.convertValue(true, this.propertyValue()),
    }, {
      value: false,
      localized: this.options.i18nConverter.convertValue(false, this.propertyValue()),
    }];
  }

  /**
   * Gets the boolean options as strings only which are supported by select options
   * @private
   * @returns {Array<Object>} - the array of boolean options
   */
  getBooleanOptionsForEnumSelect() {
    return [{
      value: 'true',
      localized: this.options.i18nConverter.convertValue(true, this.propertyValue()),
    }, {
      value: 'false',
      localized: this.options.i18nConverter.convertValue(false, this.propertyValue()),
    }];
  }

  /**
   * Gets the enum options
   * @private
   * @returns {Array<Object>} - the array of enum options
   */
  getEnumOptions() {
    return _.map(this.metadata().enum, val => ({
      value: val,
      localized: this.options.i18nConverter.convertValue(val, this.propertyValue()),
    }));
  }

  /**
   * Gets the grouped options
   * @private
   * @returns {Array<Object>} - the array of grouped options
   */
  getGroupedOptions() {
    const { groupedItems } = this.metadata();

    if (groupedItems) {
      return _.map(groupedItems, groupedItem =>
        ({
          localizedValue: this.options.i18nConverter.convertValue(
            groupedItem.name,
            this.propertyValue()
          ),
          items: _.map(groupedItem.items, item =>
            ({
              value: item,
              localizedValue: this.options.i18nConverter.convertValue(item, this.propertyValue()),
            })),
        }));
    }
    return [];
  }

  /**
   * Validates the predicate
   * @private
   * @returns {Object} - the results of the validation
   */
  validate() {
    const checkList = [
      this.checkEmpty(),
      this.checkNumber(),
      this.checkInteger(),
      this.checkEnum(),
      this.checkDate(),
      this.checkIdCount(),
      this.checkPattern(),
    ];
    const errors = _.reject(checkList, _.isNull);
    return {
      errors: errors.join(', '),
      isValid: _.isEmpty(errors),
    };
  }

  /**
   * Checks if the predicate is empty
   * @private
   * @returns {String} - an error message if the value is empty
   */
  checkEmpty() {
    return this.isValueExists() ? null : this.options.i18n.getString('Filter_Validation_Common_Required');
  }

  /**
   * Checks if the regular expression matches the instance successfully when pattern provide
   * @private
   * @returns {String} - an error message if the value is not a valid one
   */
  checkPattern() {
    const { pattern, type } = this.metadata();
    const value = this.value();

    if (type === 'string' && !_.isEmpty(pattern) && !_.isEmpty(value)) {
      const regex = _.isString(pattern) ? new RegExp(pattern) : pattern;
      let i18nString = 'Filter_Validation_Common_Pattern';

      switch (pattern) {
        // we use intTestReg to keep the alignment
        // the regex may have some issue for i18n
        // we will use i18n to check the integer when valid integer is supported by i18n
        case '^\\s*[+-]?\\s*\\d+(,\\d{3})*\\s*$':
        case '^\\s*\\d+\\s*$':
          i18nString = 'Filter_Validation_Common_Integer';
          break;
        default: break;
      }

      return regex.test(value) ? null : this.options.i18n.getString(i18nString);
    }
    return null;
  }

  checkRange(value) {
    let minAllowed = this.metadata().minimum;
    let maxAllowed = this.metadata().maximum;

    // TODO: switch to ko-schema
    if (this.metadata().isPercentValue) {
      minAllowed = _.isNumber(minAllowed) ? minAllowed : 0;
      maxAllowed = _.isNumber(maxAllowed) ? maxAllowed : 100;
    } else {
      minAllowed =
        _.isNumber(minAllowed) ? minAllowed : -9007199254740991; // Number.MIN_SAFE_INTEGER;
      maxAllowed =
        _.isNumber(maxAllowed) ? maxAllowed : 9007199254740991; // Number.MAX_SAFE_INTEGER
    }

    if (value < minAllowed || value > maxAllowed) {
      return this.options.i18n.getString('Filter_Validation_Common_Number_Range');
    }
    return null;
  }
  /**
   * Checks if input is Number
   * @private
   * @returns {String} - an error message if the number value is not a valid number
   */
  checkNumber() {
    const { type } = this.metadata();
    const valueLocal = this.value();

    if (type === 'number') {
      if (!this.options.i18n.isValidDecimal(valueLocal)) {
        return this.options.i18n.getString('Filter_Validation_Common_Number');
      }
      return this.checkRange(valueLocal);
    }
    return null;
  }

  /**
   * Checks if input is Integer
   * @private
   * @returns {String} - an error message if the integer value is not an integer
   */
  checkInteger() {
    const { type } = this.metadata();
    const value = this.value();
    if (type === 'integer') {
      if (!intTestReg.test(value)) {
        return this.options.i18n.getString('Filter_Validation_Common_Integer');
      }
      return this.checkRange(value);
    }
    return null;
  }

  /**
   * Checks if at least one option is selected
   * @private
   * @returns {String} - an error message if the enum value is not an enum
   */
  checkEnum() {
    if ((this.metadata().enum || this.metadata().groupedItems)
      && _.isArray(this.values()) && _.isEmpty(this.values())) {
      return this.options.i18n.getString('Filter_Validation_EnumSelectionRequired');
    }
    return null;
  }

  /**
   * Checks if date format is correct
   * @private
   * @returns {String} - an error message if the datetime value is not a datetime
   */
  checkDate() {
    const { type } = this.metadata();
    const value = this.value();
    if (type === 'datetime' && !_.isEmpty(value) && !(value instanceof Date)) {
      return this.options.i18n.getString('Filter_Validation_Common_Date');
    }
    return null;
  }

  /**
   * Checks if id count is within limit
   * @private
   * @returns {String} - an error message if the id count is more than allowed
   */
  checkIdCount() {
    const { type, maxCount } = this.metadata();
    const value = this.value();

    if (type === 'id' && maxCount && !_.isEmpty(value) && value.length > maxCount) {
      return this.options.i18n.getString('Filter_Validation_Common_Id_Count', { maxCount });
    }
    return null;
  }

  /**
   * Maps a value
   * @private
   * @param {Object} value - the value to map
   * @returns {String} - the mapped value
   */
  mapValue(value) {
    const metadata = this.metadata();

    // Select options does not recognize value of type boolean
    // Need to convert to string
    if (metadata.type === 'boolean' && metadata.chooseType === 'select' && _.isBoolean(value)) {
      return value.toString();
    }

    // decimal or percentage
    if (metadata.type === 'number' && typeof value === 'number') {
      return this.options.i18n.formatDecimal(value, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }

    // integer
    if (metadata.type === 'integer' && typeof value === 'number' && !metadata.skipLocalization) {
      return this.options.i18n.formatDecimal(value, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    }

    if (metadata.type !== 'datetime' || value instanceof Date) {
      return value;
    }
    const parsed = kendo.parseDate(value, this.options.shortDateFormat);
    return parsed || value;
  }
}
